// Service d'extraction des coordonnées géographiques
import type { Location, MaterialEntity, Memo } from '@/api/types';
import type { MapAsset } from '@/types/map';

class CoordinatesService {
  // Extraire les assets Location avec coordonnées
  extractLocationAssets(locations: Location[]): MapAsset[] {
    return locations
      .filter(location => location.latitude && location.longitude)
      .map(location => ({
        uuid: location.uuid,
        type: 'location' as const,
        title: location.site || location.place || 'Lieu sans nom',
        coordinates: {
          latitude: location.latitude!,
          longitude: location.longitude!
        },
        data: location
      }));
  }

  // Extraire les assets MaterialEntity avec coordonnées via provenance_location
  extractMaterialEntityAssets(entities: MaterialEntity[]): MapAsset[] {
    return entities
      .filter(entity => 
        entity.provenance_location?.latitude && 
        entity.provenance_location?.longitude
      )
      .map(entity => ({
        uuid: entity.uuid,
        type: 'materialEntity' as const,
        title: entity.appellation || 'Entité sans nom',
        coordinates: {
          latitude: entity.provenance_location!.latitude!,
          longitude: entity.provenance_location!.longitude!
        },
        data: entity
      }));
  }

  // Extraire les assets Memo avec coordonnées
  extractMemoAssets(memos: Memo[]): MapAsset[] {
    return memos
      .filter(memo => {
        // Priorité : location directe, sinon material_entity.provenance_location
        return (memo.location?.latitude && memo.location?.longitude) ||
               (memo.material_entity?.provenance_location?.latitude && 
                memo.material_entity?.provenance_location?.longitude);
      })
      .map(memo => {
        let coordinates;
        
        // Déterminer les coordonnées selon la priorité
        if (memo.location?.latitude && memo.location?.longitude) {
          coordinates = {
            latitude: memo.location.latitude,
            longitude: memo.location.longitude
          };
        } else {
          coordinates = {
            latitude: memo.material_entity!.provenance_location!.latitude!,
            longitude: memo.material_entity!.provenance_location!.longitude!
          };
        }

        return {
          uuid: memo.uuid,
          type: 'memo' as const,
          title: memo.title || 'Mémo sans titre',
          coordinates,
          data: memo
        };
      });
  }
}

export const coordinatesService = new CoordinatesService();

/**
 * Extrait le titre d'un asset selon son type
 */
export const extractAssetTitle = (asset: unknown, type: string): string => {
  switch (type) {
    case 'location':
      return (asset as Location).site || (asset as Location).place || 'Lieu sans nom';
    
    case 'materialEntity':
      return (asset as MaterialEntity).appellation || 'Entité sans nom';
    
    case 'memo':
      return (asset as Memo).title || 'Mémo sans titre';
    
    default:
      return 'Asset sans nom';
  }
};

 